import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Crown, MagnifyingGlass, BookOpen, Sparkle, FlowArrow, UsersThree, ShieldWarning,
  ArrowClockwise, PencilSimple, ChatText, Circuitry, Plus, Trash, Check, Archive, ArrowsCounterClockwise,
  ChartBar, ArrowUp, ArrowDown, Key, Copy, ArrowsClockwise,
} from "@phosphor-icons/react";

const FEATURES = [
  { key: "knowledge_base", label: "Knowledge Base", icon: BookOpen },
  { key: "ai_assistant",   label: "AI Assistant",   icon: Sparkle },
  { key: "workflows",      label: "Workflows",      icon: FlowArrow },
  { key: "leads",          label: "Leads",          icon: UsersThree },
];

const INSTRUCTION_MAX = 8000;

const INSTRUCTION_EXAMPLE = `You are the internal assistant for Acme Legal, a boutique commercial-law firm.
Tone: precise, formal, no filler.
Domain focus: contract-review checklists, deal-memo templates, and client-intake procedures already in the knowledge base.
Never speculate on legal outcomes — always cite the exact filename you draw from.
When asked to run a workflow, always summarize what it will do before proposing it.`;

export default function Platform() {
  const { user, refresh } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null); // `${org_id}:${feature}`
  const [query, setQuery] = useState("");

  // Agent-instructions dialog state
  const [editingOrg, setEditingOrg] = useState(null); // full org object or null
  const [draftInstructions, setDraftInstructions] = useState("");
  const [savingInstructions, setSavingInstructions] = useState(false);

  // Widget-config dialog state
  const [widgetOrg, setWidgetOrg] = useState(null); // org object or null
  const [widgetItems, setWidgetItems] = useState([]); // [{widget_id, label, enabled, feature}]
  const [widgetsLoading, setWidgetsLoading] = useState(false);
  const [savingWidgets, setSavingWidgets] = useState(false);

  // Inbound API key dialog state
  const [apiKeyOrg, setApiKeyOrg] = useState(null);
  const [apiKeyValue, setApiKeyValue] = useState("");
  const [apiKeyRevealed, setApiKeyRevealed] = useState(false);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [rotatingApiKey, setRotatingApiKey] = useState(false);

  const openApiKeyDialog = async (org) => {
    setApiKeyOrg(org);
    setApiKeyValue("");
    setApiKeyRevealed(false);
    setApiKeyLoading(true);
    try {
      const { data } = await api.get(`/platform/organizations/${org.org_id}/inbound-api-key`);
      setApiKeyValue(data.inbound_api_key || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load API key");
    } finally { setApiKeyLoading(false); }
  };
  const copyApiKey = async () => {
    try {
      await navigator.clipboard.writeText(apiKeyValue);
      toast.success("API key copied");
    } catch { toast.error("Could not copy"); }
  };
  const copySample = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch { toast.error("Could not copy"); }
  };
  const apiBaseUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const displayedKey = apiKeyRevealed
    ? (apiKeyValue || "YOUR_API_KEY")
    : (apiKeyValue ? `${apiKeyValue.slice(0, 6)}${"…".repeat(1)}` : "YOUR_API_KEY");
  const sampleSingleCurl = `curl -X POST '${apiBaseUrl}/api/inbound/leads' \\
  -H 'X-API-Key: ${displayedKey}' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "name": "Ada Lovelace",
    "email": "ada@lovelace.io",
    "phone": "+1-555-0100",
    "source": "n8n_enrichment",
    "grade": "A",
    "icp_score": 0.87,
    "company": {
      "name": "Analytical Engines Ltd",
      "industry": "Enterprise SaaS",
      "company_size": "51-200",
      "country": "GB"
    }
  }'`;
  const sampleBatchCurl = `curl -X POST '${apiBaseUrl}/api/inbound/leads/batch' \\
  -H 'X-API-Key: ${displayedKey}' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "leads": [
      {
        "name": "Ada Lovelace",
        "email": "ada@lovelace.io",
        "grade": "A",
        "company": { "name": "Analytical Engines Ltd" }
      },
      {
        "name": "Grace Hopper",
        "email": "grace@nautilus.io",
        "grade": "B",
        "company": { "name": "Nautilus Systems" }
      }
    ]
  }'`;
  const rotateApiKey = async () => {
    if (!apiKeyOrg) return;
    if (!window.confirm("Rotate the inbound API key? The current key will stop working immediately and n8n must be updated.")) return;
    setRotatingApiKey(true);
    try {
      const { data } = await api.post(`/platform/organizations/${apiKeyOrg.org_id}/inbound-api-key/rotate`);
      setApiKeyValue(data.inbound_api_key || "");
      setApiKeyRevealed(true);
      toast.success("New API key generated — copy it now");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rotate failed");
    } finally { setRotatingApiKey(false); }
  };

  const openWidgetsDialog = async (org) => {
    setWidgetOrg(org);
    setWidgetItems([]);
    setWidgetsLoading(true);
    try {
      const { data } = await api.get(`/platform/organizations/${org.org_id}/widget-config`);
      setWidgetItems(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load widget config");
    } finally { setWidgetsLoading(false); }
  };
  const toggleWidgetItem = (i) => {
    setWidgetItems((all) => all.map((w, idx) => idx === i ? { ...w, enabled: !w.enabled } : w));
  };
  const moveWidgetItem = (i, delta) => {
    setWidgetItems((all) => {
      const j = i + delta;
      if (j < 0 || j >= all.length) return all;
      const next = [...all];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const saveWidgets = async () => {
    if (!widgetOrg) return;
    setSavingWidgets(true);
    try {
      const items = widgetItems.map((w) => ({ widget_id: w.widget_id, enabled: !!w.enabled }));
      await api.put(`/platform/organizations/${widgetOrg.org_id}/widget-config`, { items });
      toast.success("Widget config saved");
      setWidgetOrg(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSavingWidgets(false); }
  };

  // Archive dialog state
  const [archivingOrg, setArchivingOrg] = useState(null); // full org object or null
  const [archiveConfirmName, setArchiveConfirmName] = useState("");
  const [archiving, setArchiving] = useState(false);

  // Automation-categories state
  const [categories, setCategories] = useState([]);
  const [catsLoading, setCatsLoading] = useState(true);
  const [newCatName, setNewCatName] = useState("");
  const [creatingCat, setCreatingCat] = useState(false);
  const [renameId, setRenameId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [busyCatId, setBusyCatId] = useState(null);

  // Channels state
  const [channels, setChannels] = useState([]);
  const [chansLoading, setChansLoading] = useState(true);
  const [newChanLabel, setNewChanLabel] = useState("");
  const [newChanType, setNewChanType] = useState("organic");
  const [creatingChan, setCreatingChan] = useState(false);
  const [renameChanId, setRenameChanId] = useState(null);
  const [renameChanDraft, setRenameChanDraft] = useState("");
  const [busyChanId, setBusyChanId] = useState(null);

  const loadChannels = useCallback(async () => {
    setChansLoading(true);
    try {
      const { data } = await api.get("/platform/channels");
      setChannels(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load channels");
      setChannels([]);
    } finally { setChansLoading(false); }
  }, []);

  const loadCategories = useCallback(async () => {
    setCatsLoading(true);
    try {
      const { data } = await api.get("/platform/automation-categories");
      setCategories(data);
    } catch {
      // If unauthorized (403), Navigate handles it. Otherwise noop.
    } finally {
      setCatsLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/organizations");
      setOrgs(data);
    } catch (err) {
      if (err?.response?.status === 403) return; // Navigate handles the redirect
      toast.error("Could not load organizations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.is_platform_admin) { load(); loadCategories(); loadChannels(); }
  }, [user?.is_platform_admin, load, loadCategories, loadChannels]);

  // Route guard — non-platform-admin users get redirected
  if (user && !user.is_platform_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  const toggle = async (org, feature, next) => {
    const key = `${org.org_id}:${feature}`;
    setSavingKey(key);
    // Optimistic
    const prev = { ...org.enabled_features };
    setOrgs((all) => all.map((o) =>
      o.org_id === org.org_id
        ? { ...o, enabled_features: { ...o.enabled_features, [feature]: next } }
        : o
    ));
    try {
      const { data } = await api.patch(`/platform/organizations/${org.org_id}/features`, {
        feature, enabled: next,
      });
      setOrgs((all) => all.map((o) =>
        o.org_id === org.org_id ? { ...o, enabled_features: data.enabled_features } : o
      ));
      toast.success(`${next ? "Enabled" : "Disabled"} ${feature.replace("_", " ")} for ${org.name}`);
      await refresh();
    } catch (err) {
      setOrgs((all) => all.map((o) =>
        o.org_id === org.org_id ? { ...o, enabled_features: prev } : o
      ));
      toast.error(err?.response?.data?.detail || "Could not update feature");
    } finally {
      setSavingKey(null);
    }
  };

  const openInstructionsDialog = (org) => {
    setEditingOrg(org);
    setDraftInstructions(org.agent_instructions || "");
  };

  const closeInstructionsDialog = () => {
    if (savingInstructions) return;
    setEditingOrg(null);
    setDraftInstructions("");
  };

  const saveInstructions = async () => {
    if (!editingOrg) return;
    setSavingInstructions(true);
    try {
      const { data } = await api.patch(
        `/platform/organizations/${editingOrg.org_id}/agent-instructions`,
        { agent_instructions: draftInstructions },
      );
      setOrgs((all) => all.map((o) =>
        o.org_id === editingOrg.org_id
          ? { ...o, agent_instructions: data.agent_instructions }
          : o
      ));
      toast.success(
        data.agent_instructions
          ? `Saved AI instructions for ${editingOrg.name}`
          : `Cleared AI instructions for ${editingOrg.name} — reverting to defaults`
      );
      setEditingOrg(null);
      setDraftInstructions("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save instructions");
    } finally {
      setSavingInstructions(false);
    }
  };

  const createCategory = async () => {
    const name = newCatName.trim();
    if (!name) return;
    setCreatingCat(true);
    try {
      const { data } = await api.post("/platform/automation-categories", { name });
      setCategories((all) => [...all, data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewCatName("");
      toast.success(`Added "${data.name}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add category");
    } finally {
      setCreatingCat(false);
    }
  };

  const saveRename = async (cat) => {
    const name = renameDraft.trim();
    if (!name || name === cat.name) { setRenameId(null); return; }
    setBusyCatId(cat.category_id);
    try {
      const { data } = await api.patch(
        `/platform/automation-categories/${cat.category_id}`,
        { name },
      );
      setCategories((all) => all.map((c) => c.category_id === cat.category_id ? data : c)
        .sort((a, b) => a.name.localeCompare(b.name)));
      toast.success(`Renamed to "${data.name}"`);
      setRenameId(null); setRenameDraft("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rename failed");
    } finally {
      setBusyCatId(null);
    }
  };

  const toggleCategoryActive = async (cat, next) => {
    setBusyCatId(cat.category_id);
    // Optimistic
    setCategories((all) => all.map((c) => c.category_id === cat.category_id ? { ...c, active: next } : c));
    try {
      const { data } = await api.patch(
        `/platform/automation-categories/${cat.category_id}`,
        { active: next },
      );
      setCategories((all) => all.map((c) => c.category_id === cat.category_id ? data : c));
    } catch (err) {
      setCategories((all) => all.map((c) => c.category_id === cat.category_id ? { ...c, active: !next } : c));
      toast.error(err?.response?.data?.detail || "Could not update category");
    } finally {
      setBusyCatId(null);
    }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}"? This cannot be undone.`)) return;
    setBusyCatId(cat.category_id);
    try {
      await api.delete(`/platform/automation-categories/${cat.category_id}`);
      setCategories((all) => all.filter((c) => c.category_id !== cat.category_id));
      toast.success(`Deleted "${cat.name}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete category");
    } finally {
      setBusyCatId(null);
    }
  };

  const createChannel = async () => {
    const label = newChanLabel.trim();
    if (!label) return;
    setCreatingChan(true);
    try {
      const { data } = await api.post("/platform/channels", { label, channel_type: newChanType });
      setChannels((all) => [...all, data].sort((a, b) => a.label.localeCompare(b.label)));
      setNewChanLabel("");
      toast.success(`Added "${data.label}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add channel");
    } finally { setCreatingChan(false); }
  };

  const saveChannelRename = async (ch) => {
    const label = renameChanDraft.trim();
    if (!label || label === ch.label) { setRenameChanId(null); return; }
    setBusyChanId(ch.channel_id);
    try {
      const { data } = await api.patch(`/platform/channels/${ch.channel_id}`, { label });
      setChannels((all) => all.map((c) => c.channel_id === ch.channel_id ? data : c)
        .sort((a, b) => a.label.localeCompare(b.label)));
      toast.success(`Renamed to "${data.label}"`);
      setRenameChanId(null); setRenameChanDraft("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rename failed");
    } finally { setBusyChanId(null); }
  };

  const toggleChannelActive = async (ch, next) => {
    setBusyChanId(ch.channel_id);
    setChannels((all) => all.map((c) => c.channel_id === ch.channel_id ? { ...c, active: next } : c));
    try {
      const { data } = await api.patch(`/platform/channels/${ch.channel_id}`, { active: next });
      setChannels((all) => all.map((c) => c.channel_id === ch.channel_id ? data : c));
    } catch (err) {
      setChannels((all) => all.map((c) => c.channel_id === ch.channel_id ? { ...c, active: !next } : c));
      toast.error(err?.response?.data?.detail || "Could not update channel");
    } finally { setBusyChanId(null); }
  };

  const changeChannelType = async (ch, ctype) => {
    setBusyChanId(ch.channel_id);
    try {
      const { data } = await api.patch(`/platform/channels/${ch.channel_id}`, { channel_type: ctype });
      setChannels((all) => all.map((c) => c.channel_id === ch.channel_id ? data : c));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update channel type");
    } finally { setBusyChanId(null); }
  };

  const deleteChannel = async (ch) => {
    if (!window.confirm(`Delete "${ch.label}"? This cannot be undone.`)) return;
    setBusyChanId(ch.channel_id);
    try {
      await api.delete(`/platform/channels/${ch.channel_id}`);
      setChannels((all) => all.filter((c) => c.channel_id !== ch.channel_id));
      toast.success(`Deleted "${ch.label}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete channel");
    } finally { setBusyChanId(null); }
  };

  const filtered = orgs.filter((o) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (o.name || "").toLowerCase().includes(q) || (o.slug || "").toLowerCase().includes(q);
  });

  const openArchive = (org) => {
    setArchivingOrg(org); setArchiveConfirmName("");
  };
  const closeArchive = () => {
    if (archiving) return;
    setArchivingOrg(null); setArchiveConfirmName("");
  };
  const confirmArchive = async () => {
    if (!archivingOrg) return;
    setArchiving(true);
    try {
      await api.post(`/platform/organizations/${archivingOrg.org_id}/archive`,
                     { confirm_name: archiveConfirmName });
      setOrgs((all) => all.map((o) => o.org_id === archivingOrg.org_id
        ? { ...o, archived: true, archived_at: new Date().toISOString() } : o));
      toast.success(`Archived ${archivingOrg.name}`);
      closeArchive();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not archive");
    } finally { setArchiving(false); }
  };
  const unarchiveOrg = async (org) => {
    try {
      await api.post(`/platform/organizations/${org.org_id}/unarchive`);
      setOrgs((all) => all.map((o) => o.org_id === org.org_id
        ? { ...o, archived: false, archived_at: null } : o));
      toast.success(`Restored ${org.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not unarchive");
    }
  };

  return (
    <div className="p-8 md:p-10 max-w-[1400px]" data-testid="platform-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-amber-300 flex items-center gap-1.5">
            <Crown weight="fill" className="h-3 w-3" />
            Platform admin
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">All organizations</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-xl">
            Toggle a feature off for any workspace and give each workspace its own AI Assistant persona. Their data is preserved.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="platform-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5"
          >
            <ArrowClockwise weight="bold" className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-6 rounded-md border border-amber-400/25 bg-amber-400/5 p-4 flex items-start gap-3">
        <ShieldWarning weight="duotone" className="h-5 w-5 text-amber-300 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-100/80">
          Changes take effect immediately. Users of a disabled feature will see it grayed out in their sidebar and any direct API call returns <span className="font-mono text-xs bg-graphite-900 px-1.5 py-0.5 rounded">403</span>. No data is deleted.
        </div>
      </div>

      <div className="mt-6 relative max-w-md">
        <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-secondary" />
        <input
          data-testid="platform-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by org name or slug"
          className="w-full bg-graphite-800 border border-white/10 rounded-md pl-10 pr-3 py-2 text-sm text-ink-primary placeholder:text-ink-secondary/60 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/30"
        />
      </div>

      <div className="mt-6 rounded-md border border-white/10 bg-graphite-900 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-ink-secondary">Loading organizations…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-ink-secondary" data-testid="platform-empty">
            {orgs.length === 0 ? "No organizations found." : "No matches for that search."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="platform-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-ink-secondary border-b border-white/10 bg-graphite-950/40">
                  <th className="px-5 py-3 font-medium">Organization</th>
                  <th className="px-5 py-3 font-medium">Members</th>
                  {FEATURES.map((f) => (
                    <th key={f.key} className="px-5 py-3 font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        <f.icon weight="duotone" className="h-3.5 w-3.5" />
                        {f.label}
                      </span>
                    </th>
                  ))}
                  <th className="px-5 py-3 font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      <ChatText weight="duotone" className="h-3.5 w-3.5" />
                      AI persona
                    </span>
                  </th>
                  <th className="px-5 py-3 font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      <Archive weight="duotone" className="h-3.5 w-3.5" />
                      Lifecycle
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((org) => {
                  const hasInstructions = !!(org.agent_instructions && org.agent_instructions.trim());
                  return (
                    <tr key={org.org_id} className="hover:bg-white/[0.02]" data-testid={`platform-row-${org.org_id}`}>
                      <td className="px-5 py-3">
                        <div className="text-ink-primary" data-testid={`platform-row-name-${org.org_id}`}>{org.name}</div>
                        <div className="text-[11px] text-ink-secondary font-mono">{org.slug}</div>
                      </td>
                      <td className="px-5 py-3 text-ink-secondary">{org.member_count}</td>
                      {FEATURES.map((f) => {
                        const enabled = org.enabled_features?.[f.key] !== false;
                        const busy = savingKey === `${org.org_id}:${f.key}`;
                        return (
                          <td key={f.key} className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <Switch
                                data-testid={`platform-toggle-${org.org_id}-${f.key}`}
                                checked={enabled}
                                disabled={busy}
                                onCheckedChange={(v) => toggle(org, f.key, v)}
                              />
                              <span className={`text-[11px] ${enabled ? "text-emerald-300/80" : "text-ink-secondary"}`}>
                                {enabled ? "On" : "Off"}
                              </span>
                            </div>
                          </td>
                        );
                      })}
                      <td className="px-5 py-3">
                        <button
                          data-testid={`platform-edit-instructions-${org.org_id}`}
                          onClick={() => openInstructionsDialog(org)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-graphite-800 px-3 py-1.5 text-[11px] text-ink-primary hover:bg-white/5"
                        >
                          <PencilSimple weight="bold" className="h-3 w-3" />
                          {hasInstructions ? "Edit" : "Set"}
                          {hasInstructions && (
                            <span
                              className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-emerald-300"
                              data-testid={`platform-instructions-dot-${org.org_id}`}
                              title="Custom instructions set"
                            />
                          )}
                        </button>
                        <button
                          data-testid={`platform-edit-widgets-${org.org_id}`}
                          onClick={() => openWidgetsDialog(org)}
                          className="ml-2 inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-graphite-800 px-3 py-1.5 text-[11px] text-ink-primary hover:bg-white/5"
                        >
                          <ChartBar weight="bold" className="h-3 w-3" />
                          Widgets
                        </button>
                        <button
                          data-testid={`platform-api-key-${org.org_id}`}
                          onClick={() => openApiKeyDialog(org)}
                          className="ml-2 inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-graphite-800 px-3 py-1.5 text-[11px] text-ink-primary hover:bg-white/5"
                        >
                          <Key weight="bold" className="h-3 w-3" />
                          API key
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Automation categories manager */}
      <div
        className="mt-10 rounded-md border border-white/10 bg-graphite-900 p-6 md:p-8"
        data-testid="platform-categories-section"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary flex items-center gap-1.5">
              <Circuitry weight="duotone" className="h-3 w-3" />
              Automation categories
            </div>
            <h2 className="mt-2 font-display text-2xl tracking-tight">Managed service-line taxonomy</h2>
            <p className="mt-2 text-sm text-ink-secondary max-w-xl">
              Categories are shared across every workspace. Deactivate to hide from new workflows without breaking existing ones. Delete only works when zero workflows reference the category.
            </p>
          </div>
        </div>

        <div className="mt-6 flex items-end gap-2 max-w-xl">
          <div className="flex-1 space-y-1.5">
            <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Add category</label>
            <Input
              data-testid="platform-new-category-input"
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createCategory(); } }}
              placeholder="e.g. AI Support Ticket Triage"
              className="bg-graphite-950 border-white/10 text-ink-primary"
              maxLength={100}
            />
          </div>
          <button
            data-testid="platform-new-category-add"
            onClick={createCategory}
            disabled={creatingCat || !newCatName.trim()}
            className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60"
          >
            <Plus weight="bold" className="h-4 w-4" />
            {creatingCat ? "Adding…" : "Add"}
          </button>
        </div>

        <div className="mt-6 divide-y divide-white/5 rounded-md border border-white/10 bg-graphite-950/40 overflow-hidden" data-testid="platform-categories-list">
          {catsLoading ? (
            <div className="p-6 text-center text-sm text-ink-secondary">Loading categories…</div>
          ) : categories.length === 0 ? (
            <div className="p-6 text-center text-sm text-ink-secondary">No categories yet. Add the first one above.</div>
          ) : categories.map((cat) => {
            const busy = busyCatId === cat.category_id;
            const editing = renameId === cat.category_id;
            return (
              <div
                key={cat.category_id}
                data-testid={`platform-cat-row-${cat.category_id}`}
                className="flex items-center gap-3 px-4 py-3"
              >
                <div className="flex-1 min-w-0">
                  {editing ? (
                    <div className="flex items-center gap-2">
                      <Input
                        data-testid={`platform-cat-rename-input-${cat.category_id}`}
                        value={renameDraft}
                        onChange={(e) => setRenameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); saveRename(cat); }
                          if (e.key === "Escape") { setRenameId(null); setRenameDraft(""); }
                        }}
                        maxLength={100}
                        className="bg-graphite-900 border-white/10 h-8 text-sm"
                        autoFocus
                      />
                      <button
                        data-testid={`platform-cat-rename-save-${cat.category_id}`}
                        onClick={() => saveRename(cat)}
                        disabled={busy}
                        className="rounded-full bg-white text-graphite-950 px-3 py-1 text-xs font-medium disabled:opacity-60"
                      >
                        <Check weight="bold" className="h-3 w-3 inline" /> Save
                      </button>
                      <button
                        onClick={() => { setRenameId(null); setRenameDraft(""); }}
                        className="text-[11px] text-ink-secondary hover:text-ink-primary"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span
                        data-testid={`platform-cat-name-${cat.category_id}`}
                        className={`text-sm ${cat.active ? "text-ink-primary" : "text-ink-secondary line-through"}`}
                      >
                        {cat.name}
                      </span>
                      {!cat.active && (
                        <span className="text-[10px] uppercase tracking-widest text-ink-secondary/70 border border-white/10 rounded-full px-2 py-0.5">
                          Inactive
                        </span>
                      )}
                      <span
                        data-testid={`platform-cat-usage-${cat.category_id}`}
                        className="text-[11px] text-ink-secondary"
                      >
                        · {cat.usage_count ?? 0} workflow{cat.usage_count === 1 ? "" : "s"}
                      </span>
                    </div>
                  )}
                </div>

                {!editing && (
                  <>
                    <button
                      data-testid={`platform-cat-rename-${cat.category_id}`}
                      onClick={() => { setRenameId(cat.category_id); setRenameDraft(cat.name); }}
                      disabled={busy}
                      className="text-[11px] text-ink-secondary hover:text-ink-primary inline-flex items-center gap-1 disabled:opacity-60"
                      title="Rename"
                    >
                      <PencilSimple weight="bold" className="h-3 w-3" />
                      Rename
                    </button>
                    <div className="flex items-center gap-2">
                      <Switch
                        data-testid={`platform-cat-active-${cat.category_id}`}
                        checked={cat.active}
                        disabled={busy}
                        onCheckedChange={(v) => toggleCategoryActive(cat, v)}
                      />
                      <span className={`text-[11px] ${cat.active ? "text-emerald-300/80" : "text-ink-secondary"}`}>
                        {cat.active ? "Active" : "Off"}
                      </span>
                    </div>
                    <button
                      data-testid={`platform-cat-delete-${cat.category_id}`}
                      onClick={() => deleteCategory(cat)}
                      disabled={busy || (cat.usage_count || 0) > 0}
                      title={(cat.usage_count || 0) > 0 ? "Cannot delete — workflows still use this category" : "Delete"}
                      className="text-ink-secondary hover:text-red-300 disabled:opacity-30 disabled:hover:text-ink-secondary"
                    >
                      <Trash weight="bold" className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Channels manager */}
      <div className="mt-10 rounded-md border border-white/10 bg-graphite-900 p-6 md:p-8" data-testid="platform-channels-section">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary flex items-center gap-1.5">
            <FlowArrow weight="duotone" className="h-3 w-3" />
            Channels
          </div>
          <h2 className="mt-2 font-display text-2xl tracking-tight">Managed channel taxonomy</h2>
          <p className="mt-2 text-sm text-ink-secondary max-w-xl">
            Channels are shared across every workspace and power the Campaigns multi-select.
            Deactivate to hide from new campaigns without breaking existing ones. Delete only works when zero campaigns reference the channel.
          </p>
        </div>

        <div className="mt-6 flex flex-wrap items-end gap-2 max-w-2xl">
          <div className="flex-1 min-w-[200px] space-y-1.5">
            <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Label</label>
            <Input data-testid="platform-new-channel-input" value={newChanLabel}
              onChange={(e) => setNewChanLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createChannel(); } }}
              placeholder="e.g. Podcast"
              className="bg-graphite-950 border-white/10 text-ink-primary" maxLength={60} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Type</label>
            <select data-testid="platform-new-channel-type" value={newChanType}
              onChange={(e) => setNewChanType(e.target.value)}
              className="h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
              <option value="organic">organic</option>
              <option value="paid">paid</option>
              <option value="outbound">outbound</option>
              <option value="owned">owned</option>
            </select>
          </div>
          <button data-testid="platform-new-channel-add" onClick={createChannel}
            disabled={creatingChan || !newChanLabel.trim()}
            className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60">
            <Plus weight="bold" className="h-4 w-4" />
            {creatingChan ? "Adding…" : "Add"}
          </button>
        </div>

        <div className="mt-6 divide-y divide-white/5 rounded-md border border-white/10 bg-graphite-950/40 overflow-hidden" data-testid="platform-channels-list">
          {chansLoading ? (
            <div className="p-6 text-center text-sm text-ink-secondary">Loading channels…</div>
          ) : channels.length === 0 ? (
            <div className="p-6 text-center text-sm text-ink-secondary">No channels yet. Add the first one above.</div>
          ) : channels.map((ch) => {
            const busy = busyChanId === ch.channel_id;
            const editing = renameChanId === ch.channel_id;
            const inUse = (ch.usage_count || 0) > 0;
            return (
              <div key={ch.channel_id} data-testid={`platform-chan-row-${ch.channel_id}`}
                className="flex items-center gap-3 px-4 py-3 flex-wrap">
                <div className="flex-1 min-w-[180px]">
                  {editing ? (
                    <div className="flex items-center gap-2">
                      <Input data-testid={`platform-chan-rename-input-${ch.channel_id}`}
                        value={renameChanDraft} onChange={(e) => setRenameChanDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); saveChannelRename(ch); }
                          if (e.key === "Escape") { setRenameChanId(null); setRenameChanDraft(""); }
                        }}
                        maxLength={60} className="bg-graphite-900 border-white/10 h-8 text-sm" autoFocus />
                      <button data-testid={`platform-chan-rename-save-${ch.channel_id}`}
                        onClick={() => saveChannelRename(ch)} disabled={busy}
                        className="rounded-full bg-white text-graphite-950 px-3 py-1 text-xs font-medium disabled:opacity-60">
                        <Check weight="bold" className="h-3 w-3 inline" /> Save
                      </button>
                      <button onClick={() => { setRenameChanId(null); setRenameChanDraft(""); }}
                        className="text-[11px] text-ink-secondary hover:text-ink-primary">Cancel</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span data-testid={`platform-chan-label-${ch.channel_id}`}
                        className={`text-sm ${ch.active ? "text-ink-primary" : "text-ink-secondary line-through"}`}>
                        {ch.label}
                      </span>
                      {!ch.active && (
                        <span className="text-[10px] uppercase tracking-widest text-ink-secondary/70 border border-white/10 rounded-full px-2 py-0.5">Inactive</span>
                      )}
                      <span data-testid={`platform-chan-usage-${ch.channel_id}`} className="text-[11px] text-ink-secondary">
                        · {ch.usage_count ?? 0} campaign{ch.usage_count === 1 ? "" : "s"}
                      </span>
                    </div>
                  )}
                </div>

                {!editing && (
                  <>
                    <select data-testid={`platform-chan-type-${ch.channel_id}`} value={ch.channel_type}
                      onChange={(e) => changeChannelType(ch, e.target.value)} disabled={busy}
                      className="h-7 rounded-md bg-graphite-900 border border-white/10 px-2 text-xs text-ink-primary">
                      <option value="organic">organic</option>
                      <option value="paid">paid</option>
                      <option value="outbound">outbound</option>
                      <option value="owned">owned</option>
                    </select>
                    <button data-testid={`platform-chan-rename-${ch.channel_id}`}
                      onClick={() => { setRenameChanId(ch.channel_id); setRenameChanDraft(ch.label); }}
                      disabled={busy}
                      className="text-[11px] text-ink-secondary hover:text-ink-primary inline-flex items-center gap-1 disabled:opacity-60"
                      title="Rename">
                      <PencilSimple weight="bold" className="h-3 w-3" /> Rename
                    </button>
                    <div className="flex items-center gap-2">
                      <Switch data-testid={`platform-chan-active-${ch.channel_id}`}
                        checked={ch.active} disabled={busy}
                        onCheckedChange={(v) => toggleChannelActive(ch, v)} />
                      <span className={`text-[11px] ${ch.active ? "text-emerald-300/80" : "text-ink-secondary"}`}>
                        {ch.active ? "Active" : "Off"}
                      </span>
                    </div>
                    <button data-testid={`platform-chan-delete-${ch.channel_id}`}
                      onClick={() => deleteChannel(ch)}
                      disabled={busy || inUse}
                      title={inUse ? "Cannot delete — campaigns still use this channel" : "Delete"}
                      className="text-ink-secondary hover:text-red-300 disabled:opacity-30 disabled:hover:text-ink-secondary">
                      <Trash weight="bold" className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!editingOrg} onOpenChange={(v) => { if (!v) closeInstructionsDialog(); }}>        <DialogContent
          data-testid="agent-instructions-dialog"
          className="max-w-2xl bg-graphite-900 border-white/10 text-ink-primary"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight flex items-center gap-2">
              <ChatText weight="duotone" className="h-5 w-5 text-ink-secondary" />
              AI Assistant instructions
              {editingOrg?.name && (
                <span className="text-ink-secondary font-normal text-sm">— {editingOrg.name}</span>
              )}
            </DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              Shape how the AI Assistant behaves for this workspace. Tone, persona, domain focus, tenant-specific rules — all up to you. Leave blank to use the default assistant behavior. Tool-safety rules (JSON tool calls, workflow confirmation) always take precedence.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Textarea
              data-testid="agent-instructions-textarea"
              value={draftInstructions}
              onChange={(e) => setDraftInstructions(e.target.value.slice(0, INSTRUCTION_MAX))}
              placeholder={INSTRUCTION_EXAMPLE}
              rows={12}
              className="bg-graphite-950 border-white/10 text-sm text-ink-primary placeholder:text-ink-secondary/40 font-mono focus-visible:ring-white/20"
            />
            <div className="flex items-center justify-between text-[11px] text-ink-secondary">
              <span data-testid="agent-instructions-hint">
                Tip: describe the org's business, the assistant's tone, and any hard rules.
              </span>
              <span data-testid="agent-instructions-counter">
                {draftInstructions.length} / {INSTRUCTION_MAX}
              </span>
            </div>
            <div className="rounded-md border border-white/5 bg-graphite-950 p-3 text-[11px] text-ink-secondary/80 whitespace-pre-wrap font-mono">
              <div className="uppercase tracking-widest text-[9px] text-ink-secondary mb-1.5">Example</div>
              {INSTRUCTION_EXAMPLE}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <button
              data-testid="agent-instructions-cancel"
              onClick={closeInstructionsDialog}
              disabled={savingInstructions}
              className="rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              data-testid="agent-instructions-save"
              onClick={saveInstructions}
              disabled={savingInstructions}
              className="rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60"
            >
              {savingInstructions ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Widget config dialog — platform-admin only */}
      <Dialog open={!!widgetOrg} onOpenChange={(v) => { if (!v) setWidgetOrg(null); }}>
        <DialogContent data-testid="widget-config-dialog"
          className="max-w-lg bg-graphite-900 border-white/10 text-ink-primary">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight flex items-center gap-2">
              <ChartBar weight="duotone" className="h-5 w-5 text-ink-secondary" />
              Dashboard widgets
              {widgetOrg?.name && (
                <span className="text-ink-secondary font-normal text-sm">— {widgetOrg.name}</span>
              )}
            </DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              Toggle widgets on/off and reorder them for this workspace. Only widgets whose underlying feature is enabled appear here.
            </DialogDescription>
          </DialogHeader>
          {widgetsLoading ? (
            <div className="text-sm text-ink-secondary">Loading…</div>
          ) : widgetItems.length === 0 ? (
            <div className="text-sm text-ink-secondary" data-testid="widget-config-empty">
              No widgets available (all underlying features are disabled).
            </div>
          ) : (
            <div className="rounded-md border border-white/10 overflow-hidden">
              <div className="divide-y divide-white/5">
                {widgetItems.map((w, i) => (
                  <div key={w.widget_id} data-testid={`widget-config-row-${w.widget_id}`}
                    className="grid grid-cols-[40px_1fr_auto_auto] gap-3 items-center px-4 py-3">
                    <div className="text-xs text-ink-secondary font-mono">#{i + 1}</div>
                    <div>
                      <div className="text-sm text-ink-primary">{w.label}</div>
                      {w.feature && (
                        <div className="text-[10px] uppercase tracking-widest text-ink-secondary/70">Feature · {w.feature}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button data-testid={`widget-config-up-${w.widget_id}`}
                        disabled={i === 0}
                        onClick={() => moveWidgetItem(i, -1)}
                        className="h-7 w-7 grid place-items-center rounded border border-white/10 hover:bg-white/5 disabled:opacity-30">
                        <ArrowUp className="h-3 w-3" />
                      </button>
                      <button data-testid={`widget-config-down-${w.widget_id}`}
                        disabled={i === widgetItems.length - 1}
                        onClick={() => moveWidgetItem(i, 1)}
                        className="h-7 w-7 grid place-items-center rounded border border-white/10 hover:bg-white/5 disabled:opacity-30">
                        <ArrowDown className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        data-testid={`widget-config-toggle-${w.widget_id}`}
                        checked={w.enabled}
                        onCheckedChange={() => toggleWidgetItem(i)} />
                      <span className={`text-[11px] ${w.enabled ? "text-emerald-300/80" : "text-ink-secondary"}`}>
                        {w.enabled ? "Shown" : "Hidden"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button data-testid="widget-config-cancel" onClick={() => setWidgetOrg(null)} disabled={savingWidgets}
              className="rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60">
              Cancel
            </button>
            <button data-testid="widget-config-save" onClick={saveWidgets} disabled={savingWidgets || widgetsLoading}
              className="rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60">
              {savingWidgets ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Inbound API key dialog — platform-admin only */}
      <Dialog open={!!apiKeyOrg} onOpenChange={(v) => { if (!v) { setApiKeyOrg(null); setApiKeyRevealed(false); } }}>
        <DialogContent data-testid="api-key-dialog"
          className="max-w-2xl bg-graphite-900 border-white/10 text-ink-primary max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight flex items-center gap-2">
              <Key weight="duotone" className="h-5 w-5 text-ink-secondary" />
              Inbound API key
              {apiKeyOrg?.name && (
                <span className="text-ink-secondary font-normal text-sm">— {apiKeyOrg.name}</span>
              )}
            </DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              This key authenticates n8n's <code className="text-ink-primary">POST /api/inbound/leads</code> writes for this workspace. Send it in the <code className="text-ink-primary">X-API-Key</code> header. Never share it publicly.
            </DialogDescription>
          </DialogHeader>
          {apiKeyLoading ? (
            <div className="text-sm text-ink-secondary">Loading…</div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary">Current key</label>
                <div className="mt-1.5 flex items-center gap-2">
                  <div
                    data-testid="api-key-value"
                    className="flex-1 rounded-md border border-white/10 bg-graphite-950 px-3 py-2 font-mono text-xs text-ink-primary break-all select-all">
                    {apiKeyRevealed ? apiKeyValue : (apiKeyValue ? `${apiKeyValue.slice(0, 6)}${"•".repeat(28)}` : "—")}
                  </div>
                  <button data-testid="api-key-reveal"
                    onClick={() => setApiKeyRevealed((v) => !v)}
                    className="rounded-md border border-white/10 px-3 py-2 text-xs text-ink-secondary hover:text-ink-primary hover:bg-white/5">
                    {apiKeyRevealed ? "Hide" : "Reveal"}
                  </button>
                  <button data-testid="api-key-copy"
                    onClick={copyApiKey}
                    disabled={!apiKeyValue}
                    className="inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-2 text-xs text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-40">
                    <Copy weight="bold" className="h-3 w-3" />
                    Copy
                  </button>
                </div>
                <p className="mt-2 text-[11px] text-ink-secondary">
                  Header: <code className="text-ink-primary">X-API-Key: {apiKeyRevealed ? apiKeyValue : `${(apiKeyValue || "").slice(0, 6)}…`}</code>
                </p>
              </div>
              <div className="rounded-md border border-amber-400/20 bg-amber-400/[0.03] px-3 py-2.5 text-[11px] text-amber-200/90">
                Rotating replaces the key immediately. Any n8n workflows using the current key will start failing with 401 until updated.
              </div>

              {/* ─── Sample n8n request docs ─────────────────────────────── */}
              <div data-testid="api-key-samples" className="pt-2 border-t border-white/5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
                  <FlowArrow weight="duotone" className="h-3 w-3" /> Sample n8n request
                </div>
                <p className="mt-1.5 text-[11px] text-ink-secondary">
                  Copy-paste into an n8n HTTP Request node. The key below matches your reveal/hide state above — click <span className="text-ink-primary">Reveal</span> to inline the real key before copying.
                </p>

                {/* Single-lead example */}
                <div className="mt-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] font-medium text-ink-primary flex items-center gap-2">
                      <span className="inline-flex items-center rounded-sm border border-white/10 bg-graphite-950 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest text-ink-secondary">POST</span>
                      /api/inbound/leads
                      <span className="text-ink-secondary/60 text-[10px]">Single upsert</span>
                    </div>
                    <button
                      data-testid="api-sample-single-copy"
                      onClick={() => copySample(sampleSingleCurl, "Single-lead sample")}
                      disabled={!apiKeyValue}
                      className="inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-[11px] text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-40"
                    >
                      <Copy weight="bold" className="h-3 w-3" />
                      Copy
                    </button>
                  </div>
                  <pre
                    data-testid="api-sample-single-code"
                    className="mt-1.5 rounded-md border border-white/10 bg-graphite-950 px-3 py-2.5 font-mono text-[11px] text-ink-primary overflow-x-auto leading-relaxed whitespace-pre"
                  >
{sampleSingleCurl}
                  </pre>
                </div>

                {/* Batch example */}
                <div className="mt-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] font-medium text-ink-primary flex items-center gap-2">
                      <span className="inline-flex items-center rounded-sm border border-white/10 bg-graphite-950 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest text-ink-secondary">POST</span>
                      /api/inbound/leads/batch
                      <span className="text-ink-secondary/60 text-[10px]">Bulk upsert · max 200/req</span>
                    </div>
                    <button
                      data-testid="api-sample-batch-copy"
                      onClick={() => copySample(sampleBatchCurl, "Batch sample")}
                      disabled={!apiKeyValue}
                      className="inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-[11px] text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-40"
                    >
                      <Copy weight="bold" className="h-3 w-3" />
                      Copy
                    </button>
                  </div>
                  <pre
                    data-testid="api-sample-batch-code"
                    className="mt-1.5 rounded-md border border-white/10 bg-graphite-950 px-3 py-2.5 font-mono text-[11px] text-ink-primary overflow-x-auto leading-relaxed whitespace-pre"
                  >
{sampleBatchCurl}
                  </pre>
                  <p className="mt-2 text-[10px] text-ink-secondary">
                    Batch returns per-row results (<span className="text-ink-primary">created</span> / <span className="text-ink-primary">updated</span> / <span className="text-ink-primary">failed</span>) with the exact row indexes — one bad row never aborts the batch.
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button data-testid="api-key-close"
              onClick={() => { setApiKeyOrg(null); setApiKeyRevealed(false); }}
              className="rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5">
              Close
            </button>
            <button data-testid="api-key-rotate"
              onClick={rotateApiKey}
              disabled={rotatingApiKey || apiKeyLoading}
              className="inline-flex items-center gap-1.5 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60">
              <ArrowsClockwise weight="bold" className="h-3.5 w-3.5" />
              {rotatingApiKey ? "Rotating…" : "Rotate key"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive-workspace confirmation dialog */}
      <Dialog open={!!archivingOrg} onOpenChange={(v) => { if (!v) closeArchive(); }}>
        <DialogContent
          data-testid="archive-dialog"
          className="max-w-lg bg-graphite-900 border-red-400/20 text-ink-primary"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight flex items-center gap-2">
              <Archive weight="duotone" className="h-5 w-5 text-red-300" />
              Archive workspace
            </DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              Members will lose access with a clear "workspace archived" message on next request. All data (docs, workflows, leads, invites) is preserved and instantly recoverable by clicking Restore. To confirm, type the workspace name below:
              {archivingOrg?.name && (
                <span className="block mt-2 rounded bg-graphite-950 border border-white/10 px-3 py-2 font-mono text-ink-primary text-xs">
                  {archivingOrg.name}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="archive-confirm-input"
            value={archiveConfirmName}
            onChange={(e) => setArchiveConfirmName(e.target.value)}
            placeholder="Type workspace name to confirm"
            className="bg-graphite-950 border-white/10 text-ink-primary"
            autoFocus
          />
          <DialogFooter className="gap-2">
            <button
              data-testid="archive-cancel"
              onClick={closeArchive}
              disabled={archiving}
              className="rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              data-testid="archive-confirm"
              onClick={confirmArchive}
              disabled={archiving || archiveConfirmName.trim() !== (archivingOrg?.name || "")}
              className="rounded-full bg-red-400/90 text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-red-300 disabled:opacity-40 disabled:hover:bg-red-400/90"
            >
              {archiving ? "Archiving…" : "Archive workspace"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
